import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import useViewportWidth from "../../hooks/useViewportWidth";
import { businessApi, stripeApi } from "../../services/api";

const businessIdFromProfile = (profile) => {
  if (!profile) return null;
  let raw = profile.PK ?? profile.business_id;
  if (raw == null || raw === "") return null;
  let id = String(raw);
  if (id.startsWith("BUSINESS#")) id = id.slice("BUSINESS#".length);
  return id || null;
};

const toErrorMessage = (errorLike, fallback) => {
  if (!errorLike) return fallback;
  if (typeof errorLike === "string") {
    const raw = errorLike.trim();

    if (raw.startsWith("JSONSTRING:")) {
      const payload = raw.slice("JSONSTRING:".length).trim();
      try {
        const parsed = JSON.parse(payload);
        return toErrorMessage(parsed, fallback);
      } catch (_) {
        return payload || fallback;
      }
    }

    if (raw.startsWith("{") && raw.endsWith("}")) {
      try {
        const parsed = JSON.parse(raw);
        return toErrorMessage(parsed, fallback);
      } catch (_) {
        // Keep the raw string when JSON parsing fails.
      }
    }

    return raw;
  }

  const candidates = [
    errorLike?.message,
    errorLike?.error,
    errorLike?.error?.message,
    errorLike?.response?.data?.message,
    errorLike?.response?.data?.error,
    errorLike?.response?.data?.detail,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  try {
    return JSON.stringify(errorLike);
  } catch (_) {
    return fallback;
  }
};

const PaymentInfo = () => {
  // Only to give Stripe's embedded component the full width of its card on a phone; it will
  // not render below 328px and the padding was taking the screen under that. AC-MOBILE-F21.
  const isMobile = useViewportWidth() < 900;
  const { tokens } = useAuth();
  const accessToken = tokens?.access_token

  const containerRef = useRef(null);
  const mountedComponentRef = useRef(null);
  const initIdRef = useRef(0);

  const [businessId, setBusinessId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedReady, setEmbedReady] = useState(false);
  const [embedError, setEmbedError] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  const unmountEmbeddedComponent = useCallback(() => {
    if (!containerRef.current || !mountedComponentRef.current) return;
    try {
      containerRef.current.removeChild(mountedComponentRef.current);
    } catch (_) {
      // no-op
    }
    mountedComponentRef.current = null;
  }, []);

  const initEmbeddedOnboarding = useCallback(async () => {
    if (!accessToken || !businessId) return;

    if (!publishableKey || !containerRef.current) return;

    setEmbedError("");
    setEmbedReady(false);
    setEmbedLoading(true);
    unmountEmbeddedComponent();

    const initId = ++initIdRef.current;

    try {
      const connectInstance = await loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          if (!accessToken) {
            throw new Error("Missing access token. Please sign in again.");
          }
          let session;
          try {
            session = await stripeApi.createAccountSession({ businessId }, accessToken);
          } catch (sessionError) {
            const msg = toErrorMessage(
              sessionError,
              "Failed to create Stripe account session.",
            );
            throw new Error(msg);
          }

          const secret = session?.clientSecret ?? session?.client_secret;
          if (!secret) {
            throw new Error("Account session created but no client secret was returned.");
          }
          return secret;
        },
      });

      const component = connectInstance.create("account-onboarding");

      if (typeof component.setOnLoaderStart === "function") {
        component.setOnLoaderStart(() => {
          setEmbedReady(true);
          setEmbedLoading(false);
        });
      }

      if (typeof component.setOnLoadError === "function") {
        component.setOnLoadError((loadError) => {
          const loadMessage = toErrorMessage(
            loadError?.error ?? loadError,
            "Failed to load embedded Stripe onboarding.",
          );
          setEmbedError(loadMessage);
          setEmbedLoading(false);
          toast.error(loadMessage, { position: "top-right", autoClose: 5000 });
        });
      }

      if (typeof component.setOnExit === "function") {
        component.setOnExit(async () => {
          try {
            const data = await stripeApi.completeVendorOnboarding(
              { business_id: businessId },
              accessToken,
            );
            const newVendorId =
              data.stripeVendorId ?? data.stripe_vendor_id ?? null;

            if (newVendorId) {
              try {
                await businessApi.updateProfile(
                  { business_id: businessId, stripeVendorId: newVendorId },
                  accessToken,
                );
              } catch (_) {
                // Non-fatal
              }
            }

            const submitted = Boolean(
              data.detailsSubmitted ?? data.details_submitted,
            );
            if (submitted) {
              toast.success("Payout information updated successfully!", {
                position: "top-right",
                autoClose: 4000,
              });
            }
          } catch (_) {
            // Sync failed silently
          }

          unmountEmbeddedComponent();
          setReloadNonce((n) => n + 1);
        });
      }

      if (initId !== initIdRef.current || !containerRef.current) {
        // A newer initialization started or the component unmounted — discard this one.
        return;
      }
      containerRef.current.appendChild(component);
      mountedComponentRef.current = component;
    } catch (err) {
      let message = toErrorMessage(
        err,
        "Unable to initialize embedded payout settings.",
      );

      if (/route not found|404/i.test(message)) {
        message = "Embedded Stripe is unavailable. Please try again later or contact support.";
      }

      setEmbedError(message);
      setEmbedLoading(false);
      toast.error(message, { position: "top-right", autoClose: 5000 });
    }
  }, [accessToken, businessId, publishableKey, unmountEmbeddedComponent]);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!accessToken) {
        setProfileLoading(false);
        setBusinessId(null);
        return;
      }

      setProfileLoading(true);
      try {
        const profile = await businessApi.getMyProfile(accessToken);
        if (cancelled) return;

        const id = businessIdFromProfile(profile);
        setBusinessId(id);
        if (!id) {
          toast.error("Could not determine your business account.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setBusinessId(null);
        const message = toErrorMessage(err, "Failed to load business profile.");
        toast.error(message, { position: "top-right", autoClose: 5000 });
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (profileLoading) return;
    if (!accessToken || !businessId) return;
    if (!publishableKey) return;

    initEmbeddedOnboarding();

    return () => {
      unmountEmbeddedComponent();
    };
  }, [
    profileLoading,
    accessToken,
    businessId,
    publishableKey,
    initEmbeddedOnboarding,
    unmountEmbeddedComponent,
    reloadNonce,
  ]);




  if (!accessToken) {
    return (
      <p style={{ color: "#555", fontSize: "16px", textAlign: "left" }}>
        Please sign in to manage payout settings.
      </p>
    );
  }

  if (profileLoading) {
    return (
      <p style={{ color: "#555", fontSize: "16px", textAlign: "left" }}>
        Loading your business profile...
      </p>
    );
  }

  if (!businessId) {
    return (
      <p style={{ color: "#555", fontSize: "16px", textAlign: "left" }}>
        Unable to determine your business account. Please complete your profile
        and try again.
      </p>
    );
  }

  if (!publishableKey) {
    return (
      <div>
        <h2 style={{ marginBottom: "8px", color: "#303030" }}>Payment Info</h2>
        <p style={{ marginTop: 0, color: "#555", maxWidth: "680px" }}>
          Stripe publishable key is missing. Set REACT_APP_STRIPE_PUBLISHABLE_KEY
          to enable payout management.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        alignItems: "stretch",
        minHeight: "420px",
        padding: "8px 4px 20px",
      }}
    >
      <h2
        style={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "36px",
          fontWeight: "bold",
          color: "#303030",
          margin: 0,
        }}
      >
        Payment Info
      </h2>

      <p
        style={{
          margin: 0,
          fontFamily: "Poppins, sans-serif",
          fontSize: "15px",
          color: "#666",
          textAlign: "left",
          lineHeight: 1.6,
        }}
      >
        Manage your payout settings securely through Stripe. You can update your bank account information, view payout schedules, and ensure your account is
        set up to receive payments.
      </p>

      <div
        style={{
          border: "1px solid #e5e5e5",
          borderRadius: "12px",
          // No side padding on a phone. Stripe's embedded component takes 100% of its parent's
          // width but will not render below 328px, and 12px of padding each side left it 316 of
          // the 342 the shell can offer at 390 — so the embed overran the card it sits in and
          // sat off centre. With the padding gone it fills the card, centred, and Stripe's own
          // UI brings its internal margins with it. AC-MOBILE-F21.
          padding: isMobile ? "12px 0" : "12px",
          background: "#ffffff",
        }}
      >
        {embedLoading && !embedReady ? (
          <p style={{ margin: "0 0 12px", color: "#666" }}>
            Loading Stripe onboarding...
          </p>
        ) : null}

        {embedError ? (
          <div
            style={{
              marginBottom: "12px",
              borderRadius: "10px",
              border: "1px solid #f4b2b2",
              background: "#fff5f5",
              padding: "10px 12px",
              color: "#9a2a2a",
              fontSize: "14px",
            }}
          >
            {embedError}
          </div>
        ) : null}

        <div ref={containerRef} />
      </div>


    </div>
  );
};

export default PaymentInfo;

